import {
  HighlightClipRect,
  HighlightPoint,
  HighlightShape
} from 'types/highlight';

const POLYGON_RE = /^polygon\(([^)]+)\)$/;

// Loose bound used for axes no ancestor clips; large enough to never
// constrain anything on screen while keeping SVG coordinates finite.
const CLIP_BOUND = 1e6;

export const HIGHLIGHT_CUTOUT_PADDING = 8;

// The cutout ring stroke is centered on the padded edge, so allow the
// overhang to include half of it plus a little antialiasing room.
const CLIP_OVERHANG = HIGHLIGHT_CUTOUT_PADDING + 2;
const CLIP_EDGE_EPSILON = 1;

export function getAncestorClipRect(
  element: Element
): HighlightClipRect | undefined {
  // Fixed-position elements are not clipped by scrollable ancestors.
  if (window.getComputedStyle(element).position === 'fixed') {
    return undefined;
  }

  const elementRect = element.getBoundingClientRect();
  let left = -CLIP_BOUND;
  let top = -CLIP_BOUND;
  let right = CLIP_BOUND;
  let bottom = CLIP_BOUND;
  let clipped = false;

  for (
    let ancestor = element.parentElement;
    ancestor && ancestor !== document.body;
    ancestor = ancestor.parentElement
  ) {
    const style = window.getComputedStyle(ancestor);
    const clipsX = style.overflowX !== 'visible';
    const clipsY = style.overflowY !== 'visible';

    if (clipsX || clipsY) {
      const rect = ancestor.getBoundingClientRect();
      const innerLeft = rect.left + ancestor.clientLeft;
      const innerTop = rect.top + ancestor.clientTop;
      // clientWidth/clientHeight exclude borders and scrollbars; fall back to
      // the bounding rect for elements that don't report client sizes (SVG).
      const width = ancestor.clientWidth || rect.width;
      const height = ancestor.clientHeight || rect.height;

      // On sides where the element is fully visible, let the clip breathe by
      // the cutout overhang so the decorative padding and ring aren't sliced
      // off when the element sits flush against its container. Sides the
      // element actually overflows keep the hard clip at the container edge.
      if (clipsX) {
        const innerRight = innerLeft + width;
        left = Math.max(
          left,
          elementRect.left >= innerLeft - CLIP_EDGE_EPSILON
            ? innerLeft - CLIP_OVERHANG
            : innerLeft
        );
        right = Math.min(
          right,
          elementRect.right <= innerRight + CLIP_EDGE_EPSILON
            ? innerRight + CLIP_OVERHANG
            : innerRight
        );
      }
      if (clipsY) {
        const innerBottom = innerTop + height;
        top = Math.max(
          top,
          elementRect.top >= innerTop - CLIP_EDGE_EPSILON
            ? innerTop - CLIP_OVERHANG
            : innerTop
        );
        bottom = Math.min(
          bottom,
          elementRect.bottom <= innerBottom + CLIP_EDGE_EPSILON
            ? innerBottom + CLIP_OVERHANG
            : innerBottom
        );
      }
      clipped = true;
    }

    // Anything above a fixed ancestor no longer clips it.
    if (style.position === 'fixed') {
      break;
    }
  }

  if (!clipped) {
    return undefined;
  }

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top)
  };
}

export function parsePolygonClipPath(value: string): HighlightPoint[] | null {
  const match = value.match(POLYGON_RE);
  if (!match) return null;

  const points: HighlightPoint[] = [];
  const pairs = match[1].split(',');
  for (const pair of pairs) {
    const [rawX, rawY] = pair.trim().split(/\s+/);
    const x = parseFloat(rawX);
    const y = parseFloat(rawY);
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    points.push({ x, y });
  }

  return points.length >= 3 ? points : null;
}

export function getHighlightShape(element: Element): HighlightShape {
  const clipRect = getAncestorClipRect(element);

  if (element instanceof SVGPathElement) {
    const d = element.getAttribute('d');
    const bbox = element.getBBox();
    if (d && bbox.width > 0 && bbox.height > 0) {
      return {
        rect: element.getBoundingClientRect(),
        svgPath: {
          d,
          bbox: {
            x: bbox.x,
            y: bbox.y,
            width: bbox.width,
            height: bbox.height
          }
        },
        clipRect
      };
    }
  }

  const candidates: Element[] = [element];
  if (element.parentElement) candidates.push(element.parentElement);

  for (const el of candidates) {
    const points = parsePolygonClipPath(window.getComputedStyle(el).clipPath);
    if (points) {
      return {
        rect: el.getBoundingClientRect(),
        polygonPoints: points,
        clipRect
      };
    }
  }

  return {
    rect: element.getBoundingClientRect(),
    borderRadius: window.getComputedStyle(element).borderRadius,
    clipRect
  };
}
